import React from 'react'

import moment from 'moment'
import _ from 'lodash'

import {SplitButton, Modal, ModalHeader, ModalActions, Card, Colors, H5, Button, Row, Alert, Spinner} from '../UI/index.js'
import {formSpacing, FormRow, FormStack, Input, TextArea, DatePicker} from '../UI/forms/index.js'
import {TextAreaRef} from '../UI/forms/TextArea.js'
import Addresses from './Addresses.js'
import EditCollo from './EditCollo.js'
import NewOrder from '../../../server/functions/orders/NewOrder.js'
import {emit} from 'src/socketio/socketEmit.js'
import {Address, Customer, Order, Reseller} from 'types/types.js'
import dbCodes from 'server/dbCodes.js'
import barcodes from 'server/utils/barcodes.js'
import {isEmptyCollo, showHiddenCollo} from '@utils/emptyCollo.js'
import LoginActions from 'src/actions/LoginActions.js'

type EditOrderModalCustomerProps = {
    reseller: Reseller
    customer: Customer
    planningDate: string
    viewOrder: (orderId: string) => void
    addressBook: Address[]
}

type EditOrderModalCustomerState = {
    modalIsOpen: boolean
    createOrderMode: string
    orderLoading: boolean
    orderError: string | null
    order: Order
    addressesChanged?: boolean
    referenceValid: boolean | string
    loading: boolean
    message: string
    error: string
}

export default class EditOrderModalCustomer extends React.Component<EditOrderModalCustomerProps, EditOrderModalCustomerState> {
    initialState: EditOrderModalCustomerState
    referenceInput: TextAreaRef
    addresses: Addresses

    constructor(props) {
        super(props)

        this.initialState = {
            modalIsOpen: false,
            createOrderMode: localStorage.createOrderMode || 'save',
            orderLoading: false,
            orderError: null,
            order: NewOrder(),
            referenceValid: true,
            loading: false,
            message: '',
            error: ''
        }

        this.state = structuredClone(this.initialState)
    }

    open(order?: Order) {
        const {reseller, customer, planningDate} = this.props
        const createOrderMode = localStorage.createOrderMode || 'save'

        const isNewOrder = !order

        if (!order) {
            const orderType = customer?.settings.orders.modalState?.orderType ? customer?.settings.orders.modalState?.orderType : customer.settings.orders.defaultAddresses !== 'default' ? customer.settings.orders.defaultAddresses as Reseller['settings']['orders']['defaultAddresses'] : undefined
            order = NewOrder(reseller, {selectedDate: planningDate, defaultAddresses: orderType})
        }

        if (customer) {
            order.customer = customer._id

            const contactObj = order.contact ? customer.contacts.find((contact) => contact.name === order.contact) : undefined

            order.email = contactObj?.email || customer.email || ''
            order.phone = contactObj?.phone || customer.phone || ''

            if (customer.settings.orders && isNewOrder) {
                const {modalState} = customer.settings.orders

                order.contact = modalState?.contact || ''

                const contactObj = order.contact ? customer.contacts.find((contact) => contact.name === order.contact) : undefined

                order.email = modalState?.email || contactObj?.email || customer.email || ''
                order.phone = modalState?.phone || contactObj?.phone || customer.phone || ''

                order.addresses.map((address, index) => {
                    address.minimumAge = false

                    Object.keys(dbCodes.pickupAndDeliveryOptions).forEach((option) => {
                        address[option] = false
                    })

                    address.customOptions = []

                    if (address.type === 'pickup') {
                        let pickupOptions = reseller.settings.orders.defaultPickupOptions

                        if (customer.settings.orders.defaultPickupOptions && Array.isArray(customer.settings.orders.defaultPickupOptions)) {
                            pickupOptions = customer.settings.orders.defaultPickupOptions
                        } else if (customer.settings.orders.defaultPickupOptions === 'none') {
                            pickupOptions = []
                        }

                        if (modalState?.addressesOptions?.[index]) {
                            pickupOptions = modalState.addressesOptions[index]
                        }

                        for (const option of pickupOptions) {
                            if (option in dbCodes.pickupOptions) {
                                address[option] = true
                            } else {
                                address.customOptions = address.customOptions || []
                                address.customOptions.push(option)
                            }
                        }
                    }

                    if (address.type === 'delivery') {
                        let deliveryOptions = reseller.settings.orders.defaultDeliveryOptions

                        if (customer.settings.orders.defaultDeliveryOptions && Array.isArray(customer.settings.orders.defaultDeliveryOptions)) {
                            deliveryOptions = customer.settings.orders.defaultDeliveryOptions
                        } else if (customer.settings.orders.defaultDeliveryOptions === 'none') {
                            deliveryOptions = []
                        }

                        if (modalState?.addressesOptions?.[index]) {
                            deliveryOptions = modalState.addressesOptions[index]
                        }

                        for (const option of deliveryOptions) {
                            if (option in dbCodes.pickupAndDeliveryOptions) {
                                if (option === 'minimumAge +16') {
                                    address.minimumAge = 16
                                } else if (option === 'minimumAge +18') {
                                    address.minimumAge = 18
                                } else if (option === 'minimumAge +21') {
                                    address.minimumAge = 21
                                } else {
                                    address[option] = true
                                }
                            } else {
                                address.customOptions = address.customOptions || []
                                address.customOptions.push(option)
                            }
                        }
                    }

                    if (address.customOptions?.length > 0) {
                        address.customOptions = [...new Set(address.customOptions)]
                    }
                })

                if (customer.settings.orders.generateReference) {
                    order.reference = order.reference || `${moment().format('YYMMDD')}-${Math.random().toString().substring(2, 5)}`
                }

                order.retourAddress = {
                    startTime: reseller?.settings?.account?.openingHours[moment().format('dddd').toLowerCase()] ?
                        reseller.settings.account.openingHours[moment().format('dddd').toLowerCase()].from :
                        '00:00',
                    endTime: reseller?.settings?.account?.openingHours[moment().format('dddd').toLowerCase()] ?
                        reseller.settings.account.openingHours[moment().format('dddd').toLowerCase()].till :
                        '23:59',
                    attention: '',
                    instructions: '',
                    ...customer.address
                }

                order.allowAddCollo = customer.settings.orders.allowAddCollo === 'default' ?
                    (reseller.settings.allowAddCollo || '') :
                    (customer.settings.orders.allowAddCollo || '')
            }
        }

        this.setState({modalIsOpen: true, order, createOrderMode}, () => {
            if (!order.colli.length) {
                if (isNewOrder && customer?.settings.orders.modalState) {
                    customer.settings.orders.modalState.addressesExpanded?.forEach((expanded, index) => {
                        this.addresses.addresses?.[index]?.setState({expanded})
                    })

                    if (customer.settings.orders.modalState.initialCollo) {
                        this.onClickAddCollo()
                    }
                } else {
                    this.onClickAddCollo()
                }
            }
        })
    }

    close() {
        this.setState(this.initialState)
    }

    onChangeDate(event) {
        const order = {...this.state.order}

        order.date = event.target.value

        this.setState({order})
    }

    onChangeContact(event) {
        const {customer} = this.props
        const order = {...this.state.order}

        order.contact = event.target.value

        const contact = _.find(customer?.contacts, {name: order.contact})
        if (contact) {
            order.email = contact.email || order.email
            order.phone = contact.phone || order.phone
        }

        this.setState({order})
    }

    onChangeAddresses(addresses) {
        const order = {...this.state.order}

        order.addresses = [...addresses]

        this.setState({order, addressesChanged: true})
    }

    onChangeRetourAddress(address) {
        const order = {...this.state.order}

        order.retourAddress = address

        this.setState({order})
    }

    onChangeValue(path, parser, event) {
        if (!event) {
            event = parser
            parser = undefined
        }

        const order = {...this.state.order}

        if (path === 'status') {
            order[path] = parseInt(event.target.value)
        } else if (path === 'rrule') {
            if (!event.target.value) {
                delete order.rrule
            } else {
                order.rrule = event.target.value
            }
        } else {
            if (typeof parser === 'function') {
                _.set(order, path, parser(event.target.value))
            } else {
                _.set(order, path, event.target.value)
            }
        }

        this.setState({order})
    }

    onClickAddCollo() {
        const order = {...this.state.order}

        const pickupAddressIndex = order.addresses.findIndex((address) => address.type === 'pickup')
        const deliveryAddressIndex = order.addresses.findIndex((address) => address.type === 'delivery')

        const previousCollo = order.colli[order.colli.length - 1]

        // In a new order, a new collo follows the previous one in having a barcode or not
        const barcode = !order._id && previousCollo && (!previousCollo.barcode || previousCollo.barcode === 'no barcode') ? 'no barcode' : barcodes.generate()

        // The barcode of the first collo doubles as the track & trace code of the order
        if (!order.colli.length && !order.trackTrace) {
            order.trackTrace = barcode
        }

        const hiddenColloIndex = order._id ? order.colli.findIndex(isEmptyCollo) : -1

        if (hiddenColloIndex !== -1) {
            // Show a hidden empty collo instead of adding one next to it
            order.colli = order.colli.map((collo, index) => index === hiddenColloIndex ? showHiddenCollo(collo, barcode, pickupAddressIndex, deliveryAddressIndex) : collo)
        } else {
            order.colli = [...order.colli, {
                index: order.colli.length,
                type: '',
                description: '',
                weight: 0,
                length: 0,
                width: 0,
                height: 0,
                barcode,
                pickupAddressIndex,
                deliveryAddressIndex
            }]
        }

        this.setState({order})
    }

    onClickRemoveCollo(index) {
        const order = {...this.state.order}

        const colli = [...order.colli]
        colli.splice(index, 1)
        order.colli = colli

        this.setState({order})
    }

    onChangeChecked(key) {
        const order = {...this.state.order}
        order[key] = !order[key]

        if (key === 'autoRetour' && !order.retourAddress) {
            order.retourAddress = {
                name: '',
                attention: '',
                street: '',
                nr: '',
                addition: '',
                street2: '',
                postalCode: '',
                city: '',
                country: 'NL',
                instructions: ''
            }
        }

        this.setState({order})
    }

    validate() {
        const {order} = this.state
        const {customer} = this.props

        const referenceRequired = customer.settings.orders.referenceRequired
        const referenceValid = !referenceRequired || order.reference

        if (!referenceValid) {
            this.referenceInput?.focus()
        }

        if (referenceValid) {
            return true
        } else {
            this.setState({referenceValid})
        }
    }

    async onSubmit(event) {
        event.preventDefault()

        const {order, createOrderMode} = this.state
        const {viewOrder, customer} = this.props

        if (this.validate()) {
            this.setState({loading: true, error: ''})

            try {
                let reponse
                if (order._id) {
                    reponse = await emit('orders.edit', order)
                } else {
                    reponse = await emit('orders.create', order)
                }

                const updatedOrder: Order = reponse.data

                let orderType: 'pickup' | 'deliver' | 'pickupAndDeliver'

                if (updatedOrder.addresses.length === 2) {
                    orderType = 'pickupAndDeliver'
                } else if (updatedOrder.addresses.length === 1) {
                    orderType = updatedOrder.addresses[0].type === 'delivery' ? 'deliver' : 'pickup'
                }

                let addressesOptions: string[][] = []
                let addressesExpanded: boolean[] = []

                updatedOrder.addresses.forEach((address, index) => {
                    const thisAddressOptions: string[] = []
                    Object.keys(dbCodes.pickupAndDeliveryOptions).forEach((key) => {
                        if (key.startsWith('minimumAge')) {
                            if (address.minimumAge === 16) {
                                thisAddressOptions.push('minimumAge +16')
                            } else if (address.minimumAge === 18) {
                                thisAddressOptions.push('minimumAge +18')
                            } else if (address.minimumAge === 21) {
                                thisAddressOptions.push('minimumAge +21')
                            }
                        } else if (address[key]) {
                            thisAddressOptions.push(key)
                        }
                    })

                    thisAddressOptions.push(...(address.customOptions || []))

                    addressesOptions.push(thisAddressOptions)

                    const addressComponentRef = this.addresses.addresses?.[index]
                    if (addressComponentRef) {
                        addressesExpanded[index] = !!addressComponentRef.state.expanded
                    }
                })

                customer.settings.orders.modalState = {
                    contact: updatedOrder.contact || '',
                    email: updatedOrder.email || '',
                    phone: updatedOrder.phone || '',
                    orderType,
                    addressesExpanded,
                    addressesOptions,
                    initialCollo: order.colli.length > 0 // not using updatedOrder because every created order has at least one collo
                }

                LoginActions.updateUserSettings(customer, (err) => {
                    if (err) {
                        this.setState({error: err.message})
                    }
                })

                if (createOrderMode === 'save') {
                    viewOrder(updatedOrder._id)
                    this.close()
                } else if (createOrderMode === 'saveNew') {
                    this.setState(this.initialState, () => {
                        this.open()
                    })
                } else {
                    this.close()
                }

                // @ts-expect-error address.save comes from the frontend component
                const addressesToUpdate = order.addresses.filter((address) => address.save)

                if (addressesToUpdate.length) {
                    await emit('addresses.update', addressesToUpdate)
                }
            } catch (err) {
                this.setState({loading: false, error: err.message})
            }
        }
    }

    render() {
        const {modalIsOpen, createOrderMode, orderLoading, orderError, order, loading, message, error} = this.state
        const {referenceValid} = this.state
        const {reseller, addressBook, customer} = this.props

        return (
            <Modal style={{width: 800}} show={modalIsOpen} onClose={this.close.bind(this)}>
                <ModalHeader title={order._id ? 'Bewerk rit' : 'Nieuwe rit'} onClose={this.close.bind(this)} />

                {orderLoading ?
                    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250}}>
                        <Spinner color={Colors.textDark} />
                    </div> :

                    <form onSubmit={(event) => this.onSubmit(event)}>
                        <div>
                            <FormStack style={{marginBottom: formSpacing.rowGap}}>
                                <FormRow>
                                    <Input
                                        id='contact'
                                        label='Contactpersoon'
                                        suggestions={customer ? customer.contacts?.map((contact) => contact.name) : []}
                                        suggestionFilter
                                        value={order.contact}
                                        onChange={this.onChangeContact.bind(this)}
                                    />

                                    <DatePicker
                                        label='Datum'
                                        value={order.date}
                                        disabled={!!order._id}
                                        onChange={this.onChangeDate.bind(this)}
                                    />
                                </FormRow>

                                <FormRow>
                                    <FormRow style={{flex: 1}}>
                                        <Input
                                            label='Emailadres'
                                            value={order.email}
                                            onChange={this.onChangeValue.bind(this, 'email')}
                                        />

                                        <Input
                                            label='Telefoon'
                                            type='phone'
                                            value={order.phone}
                                            onChange={this.onChangeValue.bind(this, 'phone')}
                                        />
                                    </FormRow>

                                    <TextArea
                                        label='Opmerkingen'
                                        value={order.notes}
                                        onChange={this.onChangeValue.bind(this, 'notes')}
                                    />
                                </FormRow>
                            </FormStack>

                            <Addresses
                                order={order}
                                reseller={reseller}
                                customer={customer}
                                customers={[]}
                                addresses={order.addresses}
                                addressBook={addressBook}
                                showAddAddressButton={order.type !== 'parcelPickup' && order.type !== 'parcelDelivery' && order.type !== 'retour'}
                                orderTypes={['pickup', 'delivery', 'ab']}
                                onChange={this.onChangeAddresses.bind(this)}
                                ref={(ref) => this.addresses = ref}
                            />

                            <Row style={{alignItems: 'center', marginTop: 24}}>
                                <H5 style={{marginBottom: 24, flex: 1}}>Inhoud</H5>
                            </Row>

                            {order.colli.map((collo, index) => {
                                // The server adds an empty collo to every address without colli, those stay hidden in an existing order
                                if (order._id && isEmptyCollo(collo)) {
                                    return null
                                }

                                return (
                                    <Card key={`Collo${index}`} style={{padding: 0, marginBottom: 12}}>
                                        <EditCollo
                                            order={order}
                                            collo={collo}
                                            onChange={(collo) => {
                                                const order = {...this.state.order}
                                                order.colli[index] = collo
                                                this.setState({order})
                                            }}
                                            onRemove={this.onClickRemoveCollo.bind(this, index)}
                                            reseller={reseller}
                                            customer={customer}
                                        />
                                    </Card>
                                )
                            })}

                            <Row>
                                <Button
                                    variant='outline-white'
                                    style={{marginBottom: 12}}
                                    onClick={this.onClickAddCollo.bind(this)}
                                    tooltip='Nieuwe collo'
                                    icon='mdi mdi-plus'
                                />
                            </Row>

                            <FormStack style={{marginTop: formSpacing.rowGap, marginBottom: formSpacing.rowGap}}>
                                <TextArea
                                    label='Referentie'
                                    value={order.reference}
                                    isInvalid={!referenceValid}
                                    onChange={this.onChangeValue.bind(this, 'reference')}
                                    ref={(ref) => this.referenceInput = ref}
                                />
                            </FormStack>
                        </div>

                        {message &&
                            <Alert style={{marginBottom: 6}} variant='success'>{message}</Alert>
                        }
                        {error &&
                            <Alert style={{marginBottom: 6}} variant='danger'>{error}</Alert>
                        }

                        <ModalActions>
                            <SplitButton
                                type='submit'
                                loading={loading}
                                value={createOrderMode}
                                options={[
                                    {title: 'Opslaan', value: 'save'},
                                    {title: 'Opslaan + Nieuw', value: 'saveNew'},
                                    {title: 'Opslaan + Sluiten', value: 'saveClose'}
                                    // ...order.addresses.length > 2 ? [{title: 'Aparte rit per adres', action: (event) => this.onSubmit(true, event)}] : []
                                ]}
                                onChange={(event) => {
                                    localStorage.createOrderMode = event.target.value
                                    this.setState({createOrderMode: event.target.value})
                                }}
                            >
                                {
                                    createOrderMode === 'saveNew' ?
                                        'Opslaan + Nieuw' :
                                        createOrderMode === 'saveClose' ?
                                            'Opslaan + Sluiten' :
                                            'Opslaan'
                                }
                            </SplitButton>
                        </ModalActions>
                    </form>
                }

                {orderError &&
                    <Alert style={{marginBottom: 6}} variant='danger'>{orderError}</Alert>
                }
            </Modal>
        )
    }
}
